import { useEffect, useState } from "react";
import { clearWebSession, getAuthData, getWebSession, saveWebSession } from "./session.js";
import "./login.css";
import { InstallAppButton } from "../pwa/InstallAppButton.jsx";

export function LoginGate({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [authMethod, setAuthMethod] = useState(null);
  const [checking, setChecking] = useState(() => Boolean(getAuthData()));
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const credential = getAuthData();
    if (!credential) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let active = true;
    fetch("/api/auth/session", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ initData: credential }), signal: controller.signal,
    }).then(response => {
      if (!active) return;
      if (response.ok) {
        setAuthMethod(credential.startsWith("web-session:") ? "web" : "telegram");
        setAuthenticated(true);
      }
      else {
        clearWebSession();
        setError("Не удалось подтвердить вход. Получите новый код в боте.");
      }
    }).catch(() => {
      if (active) setError("Сайт не отвечает. Проверьте соединение и попробуйте снова.");
    }).finally(() => {
      clearTimeout(timeout);
      if (active) setChecking(false);
    });
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, []);

  useEffect(() => {
    const session = getWebSession();
    if (!authenticated || authMethod !== "web" || !session) return;
    const timer = setTimeout(() => {
      clearWebSession();
      setAuthenticated(false);
      setError("Вход истёк. Получите новый код командой /web в боте.");
    }, Math.max(0, session.expiresAt * 1000 - Date.now()));
    return () => clearTimeout(timer);
  }, [authenticated, authMethod]);

  useEffect(() => {
    // Reset account-specific UI if another tab signs in/out in this browser.
    function syncSession(event) {
      if (authMethod === "telegram") return;
      if (event.storageArea === localStorage && (event.key === "metacard-web-session" || event.key === null)) {
        window.location.reload();
      }
    }
    window.addEventListener("storage", syncSession);
    return () => window.removeEventListener("storage", syncSession);
  }, [authMethod]);

  async function login(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/auth/code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
        signal: AbortSignal.timeout(15000),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Не удалось войти.");
      saveWebSession(result);
      setCode("");
      setAuthMethod("web");
      setAuthenticated(true);
    } catch (failure) {
      setError(failure.name === "TimeoutError" || failure instanceof TypeError
        ? "Сайт не отвечает. Проверьте соединение и попробуйте снова."
        : failure.message);
    } finally {
      setBusy(false);
    }
  }

  function exit() {
    clearWebSession();
    setAuthenticated(false);
  }

  if (checking) return <main className="web-login-screen" role="status">Проверяем вход…</main>;
  if (authenticated && authMethod === "telegram") return children;
  if (authenticated) return <>{children}<button className="web-login-switch" onClick={exit}>
    Выйти из веб
  </button></>;

  return <main className="web-login-screen"><section className="web-login-card">
    <span className="web-login-symbol" aria-hidden="true">✦</span>
    <h1>Вход в MetaCard</h1>
    <p>Ваши карты и прогресс доступны и в обычном браузере.</p>
    <ol><li>Отправьте <strong>/web</strong> в личный чат с <a href="https://t.me/Meta_CardBot" target="_blank" rel="noreferrer">ботом MetaCard</a>.</li>
      <li>Скопируйте полученный код и вставьте ниже.</li></ol>
    <form onSubmit={login}>
      <label htmlFor="web-login-code">Одноразовый код</label>
      <input id="web-login-code" value={code} onChange={event => setCode(event.target.value)} autoComplete="one-time-code"
        autoCapitalize="none" spellCheck={false} maxLength={64} required placeholder="Код из бота" disabled={busy} />
      <button type="submit" disabled={busy || !code.trim()}>{busy ? "Входим…" : "Войти по коду"}</button>
    </form>
    {error && <p className="web-login-error" role="alert">{error}</p>}
    <small>Код действует 10 минут и используется один раз. Этот браузер запомнит ваш аккаунт на 7 дней — в том числе после закрытия сайта.</small>
    <InstallAppButton />
  </section></main>;
}
